// --- Core ---
import {useEffect, FC, ReactElement} from "react";
import {FieldRenderProps} from "react-final-form";
import {map} from "underscore";
// --- Material UI ---
import {FormControl, FormHelperText, InputLabel, MenuItem, Select, SelectProps} from "@mui/material";
// --- SaniQ ---
import {useAppDispatch, useAppSelector} from "store";
import {fetchCountries} from "src/modules/countries/actions/Countries";

interface CountrySelectFieldProps extends FieldRenderProps<string> {
  label: ReactElement;
  required?: boolean;
  SelectDisplayProps: SelectProps["SelectDisplayProps"];
}

/**
 * A select field for selecting a country.
 */
const CountrySelectField: FC<CountrySelectFieldProps> = ({
  input,
  label,
  required,
  meta: {touched, invalid, error, submitError},
  SelectDisplayProps,
  ...custom
}) => {
  const countries = useAppSelector(state => state.countries.collection);
  const isLoading = useAppSelector(state => state.countries.isLoading);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (countries.length === 0 && !isLoading) {
      dispatch(fetchCountries());
    }
  }, [countries.length, dispatch, isLoading]);

  return (
    <FormControl
      fullWidth variant="outlined"
      error={touched && invalid}
    >
      <InputLabel
        variant="outlined"
        id={`${input.name}_label`}
        required={required}
      >
        {label}
      </InputLabel>
      <Select
        {...input}
        labelId={`${input.name}_label`}
        label={label}
        disabled={isLoading}
        error={touched && invalid}
        SelectDisplayProps={SelectDisplayProps}
        {...custom}
      >
        {
          map(countries, (country) => (
            <MenuItem value={country.id}>
              {country.translation}
            </MenuItem>
          ))
        }
      </Select>
      {
        touched && (error || submitError)&& (
          <FormHelperText>
            {error || submitError.join(", ")}
          </FormHelperText>
        )
      }
    </FormControl>
  );
};

export {CountrySelectField};
